import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { animate, motion, useInView } from "framer-motion";
import { Award, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";

function Counter({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    const c = animate(0, value, { duration: 1.4, ease: "easeOut", onUpdate: (v) => setShown(Math.round(v)) });
    return () => c.stop();
  }, [inView, value]);
  return <span ref={ref}>{shown.toLocaleString("en-IN")}</span>;
}

export default function LearnerShowcase() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const load = () => api.get("/stats/certificates").then((r) => setStats(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  if (!stats) return null;
  const has = stats.certificates > 0;

  return (
    <section className="bg-slate-100/70 py-20" data-testid="learner-showcase">
      <div className="container-q">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="grid items-center gap-10 rounded-[2rem] bg-white p-10 shadow-sm ring-1 ring-slate-200 lg:grid-cols-[1fr_1.2fr] lg:p-14">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold-600">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mint opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-mint" /></span>
              Learner showcase · live
            </p>
            {has ? (
              <>
                <p className="mt-5 font-display text-6xl font-extrabold text-navy sm:text-7xl" data-testid="showcase-certificate-count"><Counter value={stats.certificates} /></p>
                <p className="mt-3 text-lg text-slate-600" data-testid="showcase-summary">
                  Quantora course {stats.certificates === 1 ? "certificate" : "certificates"} earned by {stats.learners} {stats.learners === 1 ? "learner" : "learners"} who passed the final quiz.
                </p>
              </>
            ) : (
              <>
                <p className="mt-5 font-display text-3xl font-bold text-navy sm:text-4xl" data-testid="showcase-empty">Be among the first to earn a Quantora certificate.</p>
                <p className="mt-3 text-slate-600">Complete a free course and pass its short final quiz. This counter shows only real certificates issued.</p>
              </>
            )}
            <Link to="/learn" data-testid="showcase-learn-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-700">
              Start learning free <ArrowRight size={15} />
            </Link>
          </div>
          <ul className="space-y-3" data-testid="showcase-by-course">
            {(has ? stats.by_course : []).map((c) => (
              <li key={c.slug} className="flex items-center gap-4 rounded-2xl border border-slate-200 px-5 py-4" data-testid={`showcase-course-${c.slug}`}>
                <Award className="shrink-0 text-gold" size={22} />
                <Link to={`/learn/${c.slug}`} className="flex-1 font-semibold text-navy hover:text-mint-600">{c.title}</Link>
                <span className="font-num text-lg font-bold text-navy">{c.count.toLocaleString("en-IN")}</span>
              </li>
            ))}
            {!has && ["Short lessons with key takeaways", "A 5-question final quiz per course", "A certificate you can add to LinkedIn"].map((t) => (
              <li key={t} className="flex items-center gap-4 rounded-2xl border border-dashed border-slate-300 px-5 py-4 text-slate-600"><Award className="shrink-0 text-gold" size={20} /> {t}</li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
